import {
  getDefaultContactRoleLabel,
  normalizeContactRoleLabel,
} from "./contactRoles";
import {
  normalizeContactImageUrl,
  normalizeContactSocialUrl,
} from "./contactChannels";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const platformContactSearchCache = new Map();

function getStoredAuthToken(explicitToken) {
  if (explicitToken && String(explicitToken).trim()) {
    return String(explicitToken).trim();
  }

  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    ""
  );
}

function getAuthHeaders(explicitToken) {
  const token = getStoredAuthToken(explicitToken);
  if (!token) return {};

  const rawToken = String(token).replace(/^Bearer\s+/i, "").trim();
  return {
    Authorization: String(token).startsWith("Bearer ") ? String(token) : `Bearer ${token}`,
    "x-access-token": rawToken,
  };
}

function normalizeText(value, fallback = "") {
  if (value === undefined || value === null) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
}

export function normalizeHiringManagerContactKind(value, fallback = "hiring_manager") {
  const normalized = normalizeText(value)
    .replace(/\s+/g, "_")
    .toLowerCase();

  const fallbackNormalized = normalizeText(fallback)
    .replace(/\s+/g, "_")
    .toLowerCase();

  if (normalized.includes("recruiter")) return "recruiter";
  if (
    normalized === "contact" ||
    normalized.includes("team_member") ||
    (normalized.includes("contact") && !normalized.includes("hiring_manager"))
  ) {
    return "contact";
  }
  if (normalized.includes("hiring_manager")) return "hiring_manager";

  return fallbackNormalized || "";
}

export function isHiringManagerContactRow(row = {}) {
  const explicitKind = normalizeHiringManagerContactKind(row.contact_kind, "");
  if (explicitKind === "recruiter") return false;
  if (explicitKind === "contact") return false;
  if (explicitKind === "hiring_manager") return true;

  const source = normalizeText(row.source).toLowerCase();
  const role = normalizeText(row.role || row.title || row.position).toLowerCase();
  if (source === "ai team scan" || role === "team member") return false;
  return true;
}

export function isCompanyContactRow(row = {}) {
  const explicitKind = normalizeHiringManagerContactKind(row.contact_kind, "");
  if (explicitKind === "recruiter") return false;
  if (explicitKind === "contact") return true;
  if (explicitKind === "hiring_manager") return false;

  const source = normalizeText(row.source).toLowerCase();
  const role = normalizeText(row.role || row.title || row.position).toLowerCase();
  return source === "ai team scan" || role === "team member";
}

export function normalizeHiringManagerEntityType(value) {
  const normalized = normalizeText(value).toLowerCase();

  if (["lead", "leads"].includes(normalized)) return "lead";
  if (["company", "companies"].includes(normalized)) return "company";
  if (["client", "clients"].includes(normalized)) return "client";
  if (["job", "jobs"].includes(normalized)) return "job";
  if (["candidate", "candidates"].includes(normalized)) return "candidate";

  return "";
}

export function normalizeHiringManagerRow(row = {}) {
  if (!row || typeof row !== "object") return null;

  const name = normalizeText(
    row.name || row.hiring_manager_name || row.contact_name || row.user_name
  );
  if (!name) return null;

  const role = normalizeContactRoleLabel(
    row.role || row.title || row.position || row.hiring_manager_role || row.user_role,
    {
      contactKind: row.contact_kind,
      source: row.source,
      fallback: getDefaultContactRoleLabel({
        contactKind: row.contact_kind,
        source: row.source,
        fallback: "Hiring Manager",
      }),
    }
  );
  const source = normalizeText(row.source);

  return {
    id: row.hiring_manager_id ?? row.id ?? null,
    name,
    role,
    email: normalizeText(row.email || row.contact_email),
    phone: normalizeText(row.phone || row.contact_phone),
    linkedin_url: normalizeContactSocialUrl("linkedin", row.linkedin_url || row.linkedin),
    twitter_url: normalizeContactSocialUrl(
      "twitter",
      row.twitter_url || row.twitter || row.x_url || row.x
    ),
    facebook_url: normalizeContactSocialUrl("facebook", row.facebook_url || row.facebook),
    instagram_url: normalizeContactSocialUrl(
      "instagram",
      row.instagram_url || row.instagram
    ),
    youtube_url: normalizeContactSocialUrl("youtube", row.youtube_url || row.youtube),
    tiktok_url: normalizeContactSocialUrl("tiktok", row.tiktok_url || row.tiktok),
    profile_image: normalizeContactImageUrl(
      row.profile_image || row.avatar_url || row.avatar || row.photo_url || row.image
    ),
    source,
    entity_type: normalizeText(row.entity_type),
    entity_id: row.entity_id ?? null,
    entity_name: normalizeText(row.entity_name),
    contact_kind: normalizeHiringManagerContactKind(
      row.contact_kind,
      source.toLowerCase() === "ai team scan" || role.toLowerCase() === "team member"
        ? "contact"
        : "hiring_manager"
    ),
  };
}

export function dedupeHiringManagerRows(rows = []) {
  const seen = new Set();
  const nextRows = [];

  rows
    .map((row) => normalizeHiringManagerRow(row))
    .filter(Boolean)
    .forEach((row) => {
      const key = row.id ? `id:${row.id}` : `${row.name.toLowerCase()}::${row.role.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      nextRows.push(row);
    });

  return nextRows;
}

export function buildHiringManagerOption(raw = {}) {
  const normalized = normalizeHiringManagerRow(raw);
  if (!normalized) return null;

  return {
    id: normalized.id ?? null,
    name: normalized.name,
    role: normalized.role || getDefaultContactRoleLabel(normalized),
    email: normalized.email || "",
    phone: normalized.phone || "",
    linkedin_url: normalized.linkedin_url || "",
    twitter_url: normalized.twitter_url || "",
    facebook_url: normalized.facebook_url || "",
    instagram_url: normalized.instagram_url || "",
    youtube_url: normalized.youtube_url || "",
    tiktok_url: normalized.tiktok_url || "",
    profile_image: normalized.profile_image || "",
    source: normalized.source || "",
    entity_type: normalized.entity_type || "",
    entity_id: normalized.entity_id ?? null,
    entity_name: normalized.entity_name || "",
    contact_kind: normalized.contact_kind || "",
  };
}

export function getHiringManagerOptionKey(option = {}) {
  const normalizedName = normalizeText(option?.name).toLowerCase();
  const normalizedRole = normalizeText(option?.role || option?.title).toLowerCase();
  const normalizedEmail = normalizeText(option?.email).toLowerCase();

  if (option?.id != null && option.id !== "") return `id:${option.id}`;
  if (normalizedEmail) return `email:${normalizedEmail}`;
  return `${normalizedName}::${normalizedRole}`;
}

export function mergeHiringManagerOptions(optionLists = []) {
  const seen = new Set();
  const nextOptions = [];

  (Array.isArray(optionLists) ? optionLists : [])
    .flatMap((list) => (Array.isArray(list) ? list : [list]))
    .forEach((item) => {
      const option = buildHiringManagerOption(item);
      if (!option) return;

      const key = getHiringManagerOptionKey(option);
      if (!key || seen.has(key)) return;
      seen.add(key);
      nextOptions.push(option);
    });

  return nextOptions;
}

export function buildCompanyHiringManagerOptions(company = {}) {
  return mergeHiringManagerOptions([
    Array.isArray(company?.contacts) ? company.contacts : [],
    Array.isArray(company?.hiringManagers)
      ? company.hiringManagers
      : Array.isArray(company?.hiring_managers)
        ? company.hiring_managers
        : [],
    {
      name: company?.contact_name || company?.contact_email || company?.contact_phone || "",
      role: company?.contact_role || company?.contact_title || "Primary Contact",
      email: company?.contact_email || "",
      phone: company?.contact_phone || "",
      contact_kind: "contact",
    },
    {
      name: company?.hiring_manager_name || company?.hiring_manager || "",
      role: company?.hiring_manager_role || company?.hiring_manager_title || "Hiring Manager",
      contact_kind: "hiring_manager",
    },
  ]);
}

export async function fetchCompanyContactOptions({
  apiBase = API_BASE,
  companyId,
  token,
  signal,
} = {}) {
  const normalizedCompanyId = Number(companyId);
  if (!Number.isFinite(normalizedCompanyId) || normalizedCompanyId <= 0) {
    return [];
  }

  const response = await fetch(
    `${apiBase}/api/companies/${encodeURIComponent(String(normalizedCompanyId))}`,
    {
      method: "GET",
      headers: getAuthHeaders(token),
      credentials: "include",
      signal,
    }
  );

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      payload?.error || payload?.message || `Failed to load company contacts (${response.status})`
    );
  }

  return buildCompanyHiringManagerOptions(payload?.company || {});
}

export async function fetchHiringManagers({
  apiBase = API_BASE,
  entityType,
  entityId,
  token,
  signal,
} = {}) {
  const normalizedEntityType = normalizeHiringManagerEntityType(entityType);
  const normalizedEntityId = Number(entityId);

  if (!normalizedEntityType || !Number.isFinite(normalizedEntityId) || normalizedEntityId <= 0) {
    return [];
  }

  const url = `${apiBase}/api/hiring-managers?entityType=${encodeURIComponent(
    normalizedEntityType
  )}&entityId=${encodeURIComponent(String(normalizedEntityId))}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(token),
    credentials: "include",
    signal,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      payload?.error || payload?.message || `Failed to load hiring managers (${response.status})`
    );
  }

  return dedupeHiringManagerRows(Array.isArray(payload?.rows) ? payload.rows : []);
}

export async function searchPlatformContacts({
  apiBase = API_BASE,
  search,
  token,
  signal,
} = {}) {
  const query = normalizeText(search);
  if (!query) return [];
  const cacheKey = query.toLowerCase();
  const cached = platformContactSearchCache.get(cacheKey);
  if (cached) return cached;

  const response = await fetch(
    `${apiBase}/api/hiring-managers?search=${encodeURIComponent(query)}`,
    {
      method: "GET",
      headers: getAuthHeaders(token),
      credentials: "include",
      signal,
    }
  );
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      payload?.error || payload?.message || `Failed to search contacts (${response.status})`
    );
  }
  const rows = dedupeHiringManagerRows(Array.isArray(payload?.rows) ? payload.rows : []);
  platformContactSearchCache.set(cacheKey, rows);
  return rows;
}
